import axios from 'axios';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'https://api.coinfundmental.xyz/v1';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - JWT 토큰 추가
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - 에러 처리
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      // 토큰 만료 시 로그인 페이지로 리다이렉트
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API 메서드들
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (email: string, password: string, role?: string) =>
    api.post('/auth/register', { email, password, role }),
  getWalletNonce: (address: string) =>
    api.get('/auth/wallet/nonce', { params: { address } }),
  walletConnect: (data: {
    address: string;
    chain: string;
    signature: string;
    message?: string;
    nonce?: string;
  }) => api.post('/auth/wallet-connect', data),
};

export const assetsAPI = {
  list: (params?: { 
    chain?: string; 
    symbol?: string; 
    search?: string;
    asset_type?: string;
    limit?: number; 
    offset?: number;
  }) => api.get('/assets', { params }),
  get: (id: string) => api.get(`/assets/${id}`),
  getValuations: (id: string, limit = 10) =>
    api.get(`/assets/${id}/valuations`, { params: { limit } }),
  calculatePHS: (id: string) => api.post(`/assets/${id}/phs`),
  getMetadata: (id: string) => api.get(`/assets/${id}/metadata`),
  updateMetadata: (id: string, data: {
    metadata: Record<string, unknown>;
    merge?: boolean;
  }) => api.put(`/assets/${id}/metadata`, data),
  patchMetadata: (id: string, data: Record<string, unknown>) =>
    api.patch(`/assets/${id}/metadata`, data),
  getSampleReport: () => api.get('/assets/sample-report'),
  getMacroMetrics: (days = 30) => api.get('/assets/macro-metrics', { params: { days } }),
  getChainStats: () => api.get('/assets/chain-stats'),
  getEvents: (id: string, limit = 50) =>
    api.get(`/assets/${id}/events`, { params: { limit } }),
};

export const chainsAPI = {
  list: (includeMetadata = false) =>
    api.get('/chains', { params: { include: includeMetadata ? 'metadata' : undefined } }),
  get: (id: string) => api.get(`/chains/${id}`),
  getMetadata: (id: string) => api.get(`/chains/${id}/metadata`),
  updateMetadata: (id: string, data: {
    metadata: Record<string, unknown>;
    merge?: boolean;
  }) => api.put(`/chains/${id}/metadata`, data),
  patchMetadata: (id: string, data: Record<string, unknown>) =>
    api.patch(`/chains/${id}/metadata`, data),
};

export const valuationsAPI = {
  run: (assetId: string, modelId: string, params?: Record<string, unknown>) =>
    api.post('/valuations/run', { asset_id: assetId, model_id: modelId, params }),
  get: (runId: string) => api.get(`/valuations/${runId}`),
};

export const modelsAPI = {
  list: () => api.get('/models'),
  get: (id: string) => api.get(`/models/${id}`),
  create: (data: {
    name: string;
    description?: string;
    model_type: 'rule' | 'ml' | 'simulation';
    code_ref?: string;
    config?: Record<string, unknown>;
  }) => api.post('/models', data),
  update: (id: string, data: {
    name?: string;
    description?: string;
    code_ref?: string;
    config?: Record<string, unknown>;
  }) => api.put(`/models/${id}`, data),
  delete: (id: string) => api.delete(`/models/${id}`),
  runBacktest: (modelId: string, data: {
    asset_id: string;
    start_date: string;
    end_date: string;
    params?: {
      discount_rate?: number;
      fee_growth?: number;
      scenario?: 'conservative' | 'baseline' | 'aggressive';
    };
  }) => api.post(`/models/${modelId}/backtest`, data),
  getBacktests: (modelId: string) => api.get(`/models/${modelId}/backtests`),
  getBacktest: (modelId: string, backtestId: string) => api.get(`/models/${modelId}/backtests/${backtestId}`),
  compareBacktests: (backtestIds: string[]) => api.post('/models/backtests/compare', { backtest_ids: backtestIds }),
  getExplainability: (modelId: string, limit = 100) =>
    api.get(`/models/${modelId}/explainability`, { params: { limit } }),
};

export const graphAPI = {
  get: (params?: { asset_id?: string; period?: string; min_flow?: number }) =>
    api.get('/graph', { params }),
  simulate: (changes: Array<{
    type: 'remove' | 'add' | 'modify';
    from: string;
    to: string;
    weight?: number;
  }>) => api.post('/graph/simulate', { changes }),
};

export const alertsAPI = {
  list: () => api.get('/alerts'),
  create: (data: {
    asset_id?: string;
    rule: { type: string; op: string; value: number };
    channels: string[];
  }) => api.post('/alerts', data),
  update: (id: string, data: {
    asset_id?: string;
    rule?: { type: string; op: string; value: number };
    channels?: string[];
    enabled?: boolean;
  }) => api.put(`/alerts/${id}`, data),
  delete: (id: string) => api.delete(`/alerts/${id}`),
  toggle: (id: string) => api.patch(`/alerts/${id}/toggle`),
  getHistory: (id: string) => api.get(`/alerts/${id}/history`),
  markHistoryAsRead: (historyId: string) => api.post(`/alerts/history/${historyId}/read`),
  markAllAsRead: (alertId: string) => api.post(`/alerts/${alertId}/read-all`),
  getNotifications: (limit?: number) => api.get('/alerts/notifications', { params: { limit } }),
};

export const usersAPI = {
  getProfile: () => api.get('/users/me'),
  updateProfile: (data: {
    email?: string;
    password?: string;
    metadata?: Record<string, unknown>;
  }) => api.put('/users/me', data),
  completeOnboarding: (data: {
    role?: string;
    interested_assets?: string[];
    demo_data_allowed?: boolean;
  }) => api.post('/users/onboarding', data),
};

export const metricsAPI = {
  get: (assetId: string, params?: {
    metric?: string;
    from?: string;
    to?: string;
  }) => api.get(`/metrics/${assetId}`, { params }),
};

export const watchlistAPI = {
  list: () => api.get('/watchlist'),
  add: (assetId: string) => api.post('/watchlist', { asset_id: assetId }),
  remove: (assetId: string) => api.delete(`/watchlist/${assetId}`),
};

export const selectedAssetsAPI = {
  list: () => api.get('/selected-assets'),
  add: (assetId: string) => api.post('/selected-assets', { asset_id: assetId }),
  remove: (assetId: string) => api.delete(`/selected-assets/${assetId}`),
  clear: () => api.delete('/selected-assets'),
  batch: (assetIds: string[]) => api.post('/selected-assets/batch', { asset_ids: assetIds }),
};

export const adminAPI = {
  getJobs: () => api.get('/admin/jobs'),
  triggerIngester: (data?: { chain?: string; asset_id?: string; date_range?: { from: string; to: string } }) =>
    api.post('/admin/ingesters/trigger', data),
  evaluateAlerts: () => api.post('/admin/alerts/evaluate'),
  retryJob: (jobId: string) => api.post(`/admin/jobs/${jobId}/retry`),
  getJobLog: (jobId: string) => api.get(`/admin/jobs/${jobId}/log`),
  getStatistics: () => api.get('/admin/statistics'),
};
